namespace Application.Models {
    'use strict';

    interface ICategoryStateMap {
        [key: string]: boolean;
    }
    
    export class BlocksFilter {
        public static UNCATEGORIZED: string = "None";
        public static COOKIE_KEY_PREFIX: string = 'matrixroom';
        public static COOKIE_KEY_SOURCE: string = '_src';
        public static COOKIE_KEY_DESTINATION: string = '_dst';
        public static COOKIE_KEY_LEVELS: string = '_levels';
        public static COOKIE_KEY_CATEGORIES: string = '_categories';

        public searchInput: string;

        public categories: Models.Category[];
        public categoriesStateMap: ICategoryStateMap;
        public categoriesCleared: boolean;

        public levels: string[];
        public selectedLevel: string;
        public levelMaps: Models.LevelMaps;

        public filtered: Models.IBlockComplete[];
        public unfiltered: Models.IBlockComplete[];

        public filterTip: string;
        
        public touched: boolean;

        public customFilter: (block: IBlock) => boolean;

        private type: string;
        private $cookies: ng.cookies.ICookiesService;
        private $filter: ng.IFilterService;


        constructor(blocks: Models.IBlockComplete[], type: string, levelMaps: Models.LevelMaps, 
                    $cookies: ng.cookies.ICookiesService, $filter: ng.IFilterService, searchInput?: string) {
            this.$cookies = $cookies;
            this.$filter = $filter;
            this.filtered = blocks;
            this.unfiltered = blocks;
            this.type = type;
            this.levelMaps = levelMaps;
            this.initLevels();
            this.refreshCategories();
            if (searchInput) {
                this.searchInput = searchInput;
                this.filterByText();
            }
            this.generateFilterTip();
            this.touched = false;
            this.customFilter=undefined;
        }

        public searchText(): void {
            this.filterAll();
            this.generateFilterTip();
        }

        public changeLevel(level: string): void {
            this.touched = true;
            let levelFilter: string = this.levels.some((sourceLevel: string) => {
                return sourceLevel === level;
            }) ? level : 'SDI';
            this.selectedLevel = levelFilter;
            this.saveLevelCookie();
            this.filterAll();
            this.generateFilterTip();
        }

        public changeCategory(category: Models.Category): void {
            this.touched = true;
            this.categoriesStateMap[category.name] = !this.categoriesStateMap[category.name];
            this.categoriesCleared = this.isCategoriesCleared();
            this.saveCategoriesCookie();
            this.filterAll();
            this.generateFilterTip();
        }

        public changeAllCategories(): void {
            this.touched = true;
            Object.keys(this.categoriesStateMap).forEach((category: string) => {
                this.categoriesStateMap[category] = false;
            });
            this.categoriesCleared = true;
            this.saveCategoriesCookie();
            this.filterAll();
            this.generateFilterTip();
        }

        public refreshCategories(): void {
            this.initCategoriesAndStateMap();
            this.orderCategories();
            this.getCategoriesFromCookies();
            this.categoriesCleared = this.isCategoriesCleared();
            this.filterAll();
            this.generateFilterTip();
        }

        private filterAll(): void {
            this.filterByLevel()
                .filterByCategory()
                .filterByText()
                .applyCustomFilter();
        }

        private filterByLevel(): BlocksFilter {
            if (this.type === 'source')
                this.filtered = this.levelMaps.filterByInputLevel(this.unfiltered, this.selectedLevel);
            else 
                this.filtered = this.levelMaps.filterByOutputLevel(this.unfiltered, this.selectedLevel);
            return this;
        }

        private filterByCategory(): BlocksFilter {
            if (!this.isCategoriesCleared()) {
                let filtered: IBlockComplete[] = this.filtered.filter((block: IBlockComplete) => {
                    return block.categories.map((selected: Models.Category) => {
                            return this.categoriesStateMap[selected.name];
                    }).reduce((someTrue: boolean, categoryTrue: boolean) => {
                        return someTrue || categoryTrue;
                    }, false);
                });
                this.filtered = filtered;
            }
            return this;
        }

        private filterByText(): BlocksFilter {
            let result: IBlockComplete[];
            let search: string = this.searchInput;
            if (search && search.length >=  2) {
                search = search.toUpperCase(); 
                result = this.filtered.filter((block: IBlockComplete) => { 
                    let node: Models.INetworkNode = block.networkNode; 
                    return node.alias && node.alias.toUpperCase().indexOf(search) > -1 || 
                        node.name && node.name.toUpperCase().indexOf(search) > -1 ||
                        node.mftSerialNumber && node.mftSerialNumber.toString().toUpperCase().indexOf(search) > -1 ||
                        node.type && node.type.name.toUpperCase().indexOf(search) > -1 ||
                        block.name && block.name.toUpperCase().indexOf(search) > -1; 
                });
                this.filtered = result;
            }
            return this;
        }

        private initLevels(): void {
            this.levels = this.type === 'source' ?
                this.levelMaps.extractCurrentInputLevels(this.filtered) :  this.levelMaps.extractCurrentOutputLevels(this.filtered);
            this.getLevelsFromCookies();
            this.saveLevelCookie();

        }

        private initCategoriesAndStateMap(): void {
            this.categoriesStateMap = {};
            let categoriesFromBlocks: Models.Category[] = [];
            this.unfiltered.forEach((block: IBlockComplete) => {
                block.categories.forEach((category: Models.Category) => {
                    if (!categoriesFromBlocks.some((blockCategory: Models.Category) => {
                        return blockCategory.name === category.name;
                    })) {
                        categoriesFromBlocks.push(category);
                        if (!this.categoriesStateMap.hasOwnProperty(category.name))
                            this.categoriesStateMap[category.name] = false;
                    }
                });
            });
            this.categories = categoriesFromBlocks;            
        }

        private isCategoriesCleared(): boolean {
            return Object.keys(this.categoriesStateMap).every((key: string) => {
                return this.categoriesStateMap.hasOwnProperty(key) && this.categoriesStateMap[key] === false;
            });
        }

        private orderCategories(): void {
            this.categories.sort((category1: Models.Category, category2: Models.Category) => {
                if (category1.name < category2.name)
                    return -1;
                if (category1.name > category2.name)
                    return 1;
                return 0;
            });
        }

        private selectCategories(cookieCategories: string[]): void {
            cookieCategories.forEach((category: string) => {
                if (category !== "" && this.categoriesStateMap[category] !== undefined)
                    this.categoriesStateMap[category] = true;
            });
        }

        /**
         * Cookies methods
         */

        private getLevelsFromCookies(): string {
            let levelCookieKey: string = this.type === 'source' ? 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_SOURCE  + BlocksFilter.COOKIE_KEY_LEVELS : 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_DESTINATION  + BlocksFilter.COOKIE_KEY_LEVELS;

            let cookieLevel: string = this.$cookies.get(levelCookieKey);
            if (cookieLevel !== undefined && this.levels.some((level: string) => {
                return level === cookieLevel;
            })) {
                this.selectedLevel = cookieLevel;
            } else if (this.levels.some((level: string) => {
                return level === "SDI";
            })) {
                this.selectedLevel = "SDI";
            } else if (this.levels.length > 0) {
                this.selectedLevel = this.levels[0];
            }
            return this.selectedLevel;
        }

        private getCategoriesFromCookies(): string[] {
            let categoriesCookieKey: string = this.type === 'source' ? 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_SOURCE  + BlocksFilter.COOKIE_KEY_CATEGORIES : 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_DESTINATION  + BlocksFilter.COOKIE_KEY_CATEGORIES;
            
            if (this.$cookies.get(categoriesCookieKey)) {
                let cookieCategories: string[] = this.$cookies.get(categoriesCookieKey)
                    .split("|");
                this.selectCategories(cookieCategories);
                return cookieCategories;
            }
        }

        private saveLevelCookie(): void {            
            let levelCookieKey: string = this.type === 'source' ? 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_SOURCE  + BlocksFilter.COOKIE_KEY_LEVELS : 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_DESTINATION  + BlocksFilter.COOKIE_KEY_LEVELS;
            this.$cookies.put(levelCookieKey, this.selectedLevel);
        }
        
        private saveCategoriesCookie(): void {
            let categoriesCookieKey: string = this.type === 'source' ? 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_SOURCE  + BlocksFilter.COOKIE_KEY_CATEGORIES : 
                BlocksFilter.COOKIE_KEY_PREFIX + BlocksFilter.COOKIE_KEY_DESTINATION  + BlocksFilter.COOKIE_KEY_CATEGORIES;

            let selectedCategories: string = "";
            if (this.categoriesStateMap) {
                Object.keys(this.categoriesStateMap).filter((category: string) => {
                    return this.categoriesStateMap[category];
                }).forEach((category: string) => {
                    selectedCategories = selectedCategories + category + "|";
                });
                selectedCategories = selectedCategories.substr(0, selectedCategories.length - 1);
            }
            this.$cookies.put(categoriesCookieKey, selectedCategories);
        }

        private generateFilterTip(): void {
            this.filterTip = "";
            if (this.searchInput && this.searchInput.length > 0)
                this.filterTip += "<b>Text</b>: " + this.$filter<Filters.IEscapeHtmlFilter>('escapeHtml')(this.searchInput);

            if (this.selectedLevel !== undefined)
                this.filterTip += " <b>Level</b>: " + this.selectedLevel;

            Object.keys(this.categoriesStateMap).sort((category1: string, category2: string) => {
                if (category1 < category2)
                    return -1;
                if (category1 > category2)
                    return 1;
                return 0;
            }).filter((category: string) => {
                return this.categoriesStateMap[category];
            }).forEach((category: string, index: number, filtered: string[]) => {
                if (index === 0)
                    this.filterTip += " <b>Categories</b>: ";
                
                this.filterTip += " " + category + ",";
                
                if (index === filtered.length - 1) {
                    this.filterTip = this.filterTip.slice(0, -1);
                }
            });

            if (this.filterTip.length === 0) {
                this.filterTip = "<i>No filters applied</i>";
            }
        }

        private applyCustomFilter() {
            if (this.customFilter) {
                this.filtered = this.filtered.filter(this.customFilter);
            }
        }
    }
}